import {ReactNode, useEffect, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';
import {FormPhoneField} from '@common/ui/forms/input-field/phone-field/phone-field';
import {FormTextField} from '../../../ui/forms/input-field/text-field/text-field';
import {Button} from '../../../ui/buttons/button';
import {Form} from '../../../ui/forms/form';
import {Trans} from '../../../i18n/trans';
import {useBootstrapData} from '../../../core/bootstrap-data/bootstrap-data-context';
import {onFormQueryError} from '../../../errors/on-form-query-error';
import {useHandleLoginSuccess} from '../../requests/use-login';
import {
  PhoneResetSessionResponse,
  useResetPhonePassword,
  useStartPhonePasswordReset,
} from '../../requests/phone-password-reset';
import {OtpCodeField} from './otp-code-field';

interface PhoneStep {
  phone: string;
}
interface ResetStep {
  code: string;
  password: string;
  password_confirmation: string;
}

interface Props {
  // when the user arrives from the login "must reset" path we already know the
  // number, so the code step is shown straight away and the code is sent for
  // them - they never re-type the number
  initialPhone?: string;
  // when the user clicked "Forgot password?" we pre-fill the number they had
  // typed but stay on step 1 (no code is sent until they confirm)
  prefillPhone?: string;
  notice?: ReactNode;
}

/**
 * Reset a password by phone OTP: enter the number, receive a code, then set a
 * new password. On success the user is signed straight in.
 */
export function PhonePasswordResetForm({initialPhone, prefillPhone, notice}: Props) {
  const {data: {environment}} = useBootstrapData();
  const handleSuccess = useHandleLoginSuccess();

  // if the number is already known (must-reset), jump straight to the code step
  const [phone, setPhone] = useState<string | null>(initialPhone ?? null);

  const phoneForm = useForm<PhoneStep>({
    defaultValues: {phone: initialPhone ?? prefillPhone},
  });
  const resetForm = useForm<ResetStep>();
  const start = useStartPhonePasswordReset();
  const reset = useResetPhonePassword();

  // send the code once when we already have the number (must-reset path). Runs
  // in the background - the code step is already visible - so a slow or failed
  // send never blocks the form; the user can resend.
  const autoSent = useRef(false);
  useEffect(() => {
    if (initialPhone && !autoSent.current) {
      autoSent.current = true;
      start.mutate({phone: initialPhone});
    }
    // start.mutate is stable; intentionally only keyed on initialPhone
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPhone]);

  const resend = () => {
    if (phone) start.mutate({phone});
  };

  if (!phone) {
    return (
      <Form
        form={phoneForm}
        onSubmit={({phone}) => {
          start.mutate(
            {phone},
            {
              onSuccess: () => setPhone(phone),
              onError: r => onFormQueryError(r, phoneForm),
            },
          );
        }}
      >
        {notice ? <div className="mb-24 text-sm text-danger">{notice}</div> : null}
        <div className="mb-32 text-sm">
          <Trans message="Enter your phone number and we will send you a verification code to reset your password." />
        </div>
        <FormPhoneField
          className="mb-32"
          name="phone"
          type="tel"
          label={<Trans message="Phone" />}
          onlyCountries={environment.ONLY_COUNTRIES?.split(',')}
          excludeCountries={environment.EXCLUDED_COUNTRIES?.split(',')}
          initialCountry={environment.ONLY_COUNTRIES?.split(',')[0]}
          required
        />
        <Button
          className="block w-full"
          type="submit"
          variant="flat"
          color="primary"
          size="md"
          disabled={start.isPending}
        >
          <Trans message="Send code" />
        </Button>
      </Form>
    );
  }

  return (
    <Form
      form={resetForm}
      onSubmit={({code, password, password_confirmation}) => {
        reset.mutate(
          {phone, code, password, password_confirmation},
          {
            onSuccess: (response: PhoneResetSessionResponse) => {
              // ownership proven + new password set: the server signed us in
              handleSuccess(response);
            },
            onError: r => onFormQueryError(r, resetForm),
          },
        );
      }}
    >
      {notice ? <div className="mb-24 text-sm text-danger">{notice}</div> : null}
      <div className="mb-32 text-sm">
        <Trans message="Enter the code we sent you and choose a new password." />
      </div>
      <OtpCodeField className="mb-20" autoFocus />
      <FormTextField
        className="mb-20"
        name="password"
        type="password"
        autoComplete="new-password"
        label={<Trans message="New password" />}
        required
      />
      <FormTextField
        className="mb-32"
        name="password_confirmation"
        type="password"
        autoComplete="new-password"
        label={<Trans message="Confirm new password" />}
        required
      />
      <Button
        className="block w-full"
        type="submit"
        variant="flat"
        color="primary"
        size="md"
        disabled={reset.isPending}
      >
        <Trans message="Reset password" />
      </Button>
      <Button
        variant="text"
        size="xs"
        className="mt-14"
        onClick={resend}
        disabled={start.isPending}
        type="button"
      >
        <Trans message="Resend code" />
      </Button>
    </Form>
  );
}
